/**
 * Staff intake — the technical-field data layer (Step-2 Slice 2).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Slice 2 = the STAFF/technical fields the client never fills:
 *    1. NORTH bearing → the client ROOT doc `clients/{id}.profile.north_rotation`
 *       (Break 1 fix — the engine reads it there at ensureGrids.js:29 /
 *       loadInputs.js:51, NOT under answers). Per OQ-6 north lives on the
 *       client root profile, not per-property.
 *    2. FLOOR SCAFFOLD + per-floor ENTRANCES → `answers.floors[]` on the
 *       intake doc `clients/{id}/intake/L2B_R1`. Each floor: { id, name,
 *       main_entry_label (32-pada code), entrances[] {label,name,type} }.
 *       This is the scaffold Slice 3 attaches boundaries to.
 *
 *  Both write WITH merge — Slice 1's client-owned `answers.*` keys
 *  (client_name, presenting_concerns, persons …) and these staff keys live on
 *  the SAME doc and must never clobber each other. Firestore map-merge keeps
 *  sibling keys; only the keys present in a given write are replaced. So a
 *  client re-save (which writes `answers` WITHOUT floors) preserves the staff
 *  `answers.floors`, and a staff save (which writes ONLY `answers.floors` /
 *  `answers.site_location`) preserves the client keys.
 *
 *  ALSO HERE (owner-locked, additive): `answers.site_location` — the
 *  AV-workflow site-coordinates convenience (the engine ignores it). The pure
 *  parse/format brain is `siteLocation.ts`; this module owns the Firestore IO.
 *
 *  Pure functions (everything above the Firebase import) carry NO Firebase
 *  import so the Node test runner loads them via `_tsload.cjs`. The impure
 *  `loadStaffIntake` / `saveNorthRotation` / `saveFloors` / `saveSiteLocation`
 *  sit at the bottom and import `db`.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { L2B_R1_TIER, type MediaInput } from './intake';
import type { SiteLocation } from './siteLocation';
import type { Pt } from './vastuGrid';
import {
  type FloorPlan,
  type NorthScreenshot,
  floorPlanFromDoc,
  floorPlanDocBody,
  northScreenshotFromDoc,
  northScreenshotDocBody,
} from './floorPlan';
import { cleanBoundary } from './gridOverlay';
import { edgePointAt, nearestEdge } from './doorGeometry';

/* ─────────────────────────── the entrance palette ─────────────────────── */

/**
 * The 32-pada entrance codes (N1–N8, E1–E8, S1–S8, W1–W8) — the canonical set
 * the engine's step1_entrance reads (effect text comes from the canonical
 * AV_VASTU_EFFECTS_DB at render time; intake supplies only the CODE). Inlined
 * (not imported from outside src/) so the build has no out-of-tree dep —
 * mirrors the retired L1B form's entrance data + entrance_codes_32pada.json.
 */
export const ENTRANCE_CODES = [
  'N1', 'N2', 'N3', 'N4', 'N5', 'N6', 'N7', 'N8',
  'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8',
  'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8',
  'W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8',
] as const;
export type EntranceCode = (typeof ENTRANCE_CODES)[number];

/** Is `s` one of the 32 valid pada codes? */
export function isEntranceCode(s: unknown): s is EntranceCode {
  return (
    typeof s === 'string' &&
    (ENTRANCE_CODES as readonly string[]).includes(s.toUpperCase())
  );
}

/**
 * PERIMETER door vocabulary — the canonical 5 (memory:
 * reference_entrance_vocabulary — windows EXCLUDED, balcony openings INCLUDED).
 * A perimeter door sits ON the traced boundary wall (edge-slide width-rectangle).
 * `type` is a render label only (step1_entrance prefers the per-entrance list
 * over the single main label).
 */
export const PERIMETER_ENTRANCE_TYPES = [
  'main_door',
  'secondary_door',
  'back_door',
  'servant_entry',
  'balcony_opening',
] as const;

/**
 * INTERNAL door vocabulary (memory: reference_entrance_vocabulary 2026-06-07).
 * An INTERNAL door sits INSIDE the traced footprint — the floor entry is off a
 * staircase or lift lobby, so the actual door is not on the perimeter. It is a
 * rectangular width-door (free segment with two endpoints + width), placed and
 * rotated freely; its covered padas are read by DIRECTION from the centroid
 * (`padasForInteriorSegment`) — every 32-radial sector its angular span touches.
 *
 * BALCONY (owner confirmed 2026-06-07 — ADD `balcony_entry` to the internal set):
 * an internal `balcony_entry` and the perimeter `balcony_opening` coexist — a
 * balcony opening on an outer wall stays `balcony_opening` (perimeter); a balcony
 * entry sitting inside the footprint (e.g. a recessed balcony off a lobby) is the
 * internal `balcony_entry`. So the internal set is FOUR: staircase / generic
 * internal / lift / balcony entries.
 */
export const INTERNAL_ENTRANCE_TYPES = [
  'staircase_entry',
  'internal_entry',
  'lift_entry',
  'balcony_entry',
] as const;

/**
 * The full type vocabulary (perimeter ∪ internal). `EntranceType` accepts any
 * of these; the placement of a door (`'perimeter' | 'internal'`) decides which
 * subset the UI offers. The engine ignores `type` entirely — it reads the pada
 * `label` — so a new internal type never needs an engine change.
 */
export const ENTRANCE_TYPES = [
  ...PERIMETER_ENTRANCE_TYPES,
  ...INTERNAL_ENTRANCE_TYPES,
] as const;
export type EntranceType = (typeof ENTRANCE_TYPES)[number];

export function isEntranceType(s: unknown): s is EntranceType {
  return (
    typeof s === 'string' &&
    (ENTRANCE_TYPES as readonly string[]).includes(s)
  );
}

/** A door is either ON the perimeter wall or placed freely INSIDE the boundary. */
export const DOOR_PLACEMENTS = ['perimeter', 'internal'] as const;
export type DoorPlacement = (typeof DOOR_PLACEMENTS)[number];

export function isDoorPlacement(s: unknown): s is DoorPlacement {
  return s === 'perimeter' || s === 'internal';
}

/** Is `t` an INTERNAL-only entrance type? (drives which dropdown a door shows.) */
export function isInternalEntranceType(t: unknown): t is (typeof INTERNAL_ENTRANCE_TYPES)[number] {
  return (
    typeof t === 'string' &&
    (INTERNAL_ENTRANCE_TYPES as readonly string[]).includes(t)
  );
}

/* ─────────────────────────── form-state types ─────────────────────────── */

/**
 * A single ENGINE-FACING entrance as persisted — one per COVERED pada. The
 * engine reads each `.label` (step1_entrance) and emits one finding per element,
 * so a door spanning two padas persists as TWO of these (owner rule). Derived
 * from the rich `DoorInput` markers; never hand-typed.
 */
export interface EntranceInput {
  /** 32-pada code, e.g. 'N8'. The one required field. */
  label: string;
  /** Free name, e.g. "Front door". Optional. */
  name?: string;
  /** 5-type vocab. Optional (label only). */
  type?: EntranceType | '';
  /**
   * LEGACY Stage-3 (point-pin) field: the click POINT that derived `label`. No
   * longer written by the door-rectangle model (Stage 3b), but round-tripped so
   * a floor saved under Stage 3 is never destroyed. The engine ignores it.
   */
  point?: Pt;
}

/**
 * A DOOR marker as persisted. Two placements share this shape:
 *
 *  · PERIMETER (Stage 3b — door = width-rectangle ON the wall): `edge_index` +
 *    `parametric_t` + `width_image_px` locate the opening on the traced
 *    boundary; covered padas = every pada the WIDTH spans (one finding each).
 *  · INTERNAL (2026-06-07 — door INSIDE the footprint, off a staircase/lift
 *    lobby): a rectangular width-door placed FREELY inside the plot — a SEGMENT
 *    with two free endpoints `a {x,y}` / `b {x,y}` + `width_image_px`, rotatable
 *    (the endpoints set its orientation + length). Its covered padas are read by
 *    DIRECTION from the centroid (`padasForInteriorSegment`) — every 32-radial
 *    sector its angular span subtends, one finding each (the perimeter "every
 *    covered pada" rule, by DIRECTION not boundary position). `edge_index` /
 *    `parametric_t` are absent (the door is not on a wall). `at` is the LEGACY
 *    point shape (pre-segment internal doors) — read for back-compat and
 *    migrated to a tiny segment on load; never written by the segment model.
 *
 * This is the rich UI shape that re-renders on reload; the engine-facing
 * `entrances[]` is DERIVED from it. The engine ignores `doors[]` entirely — it
 * reads `entrances[]`/`main_entry_label`, so the new placement/type carry as
 * metadata with no engine change.
 */
export interface DoorInput {
  /** which boundary edge the door snaps to (PERIMETER only). */
  edge_index?: number;
  /** centre position along that edge, 0..1 (PERIMETER only). */
  parametric_t?: number;
  /** door opening width in image pixels (PERIMETER + INTERNAL — band thickness). */
  width_image_px?: number;
  /** first free endpoint of the internal door segment (INTERNAL only). */
  a?: Pt;
  /** second free endpoint of the internal door segment (INTERNAL only). */
  b?: Pt;
  /** LEGACY internal point (pre-segment). Migrated to a tiny segment on load. */
  at?: Pt;
  /** 'perimeter' (default, back-compat) | 'internal'. */
  placement?: DoorPlacement;
  /** type vocab (perimeter or internal set, per placement). Optional. */
  type?: EntranceType | '';
  /** optional free name. */
  name?: string;
  /** true for the floor's principal/main entry (exactly one). */
  principal?: boolean;
  /**
   * Module 4 (Phase 2) — the grid id that was active when this door was
   * placed. Optional — old shapes without it remain valid.
   */
  gridId?: string;
}

/**
 * A single AUO fixture as persisted (Slice 5 — the AUO confirm-loop). The engine
 * reads ONLY `name` + `zone` (step2_auo.js:135); the rest is review/UI metadata
 * the COMPOSE SEAM (`confirmedFixturesOnly`) strips before the engine. The
 * fixtures ride INSIDE each floor object so they round-trip through the SAME
 * `saveFloors` one-owner-of-the-floors-array path as boundary/doors (a separate
 * fixtures write would be clobbered by the next floors save — Slice-2 invariant).
 */
export interface FixtureInput {
  /** the AUO name (palette label or inline-added). Normalises via the engine. */
  name: string;
  /** the FIRST 16-zone code (e.g. 'NE'), DERIVED client-side. Kept for the
   *  single-zone compose/callable path (= zones[0]). */
  zone: string;
  /** the FULL set of 16-zones the AUO SHAPE overlaps (multi-zone — a bed/cabinet
   *  spanning a zone line covers more than one). Compass order. */
  zones?: string[];
  /** the AUO SHAPE (rect-with-rotation or circle) in image-pixel space — lets the
   *  marker re-render + the zones re-derive on reload. The engine ignores it. */
  region?: unknown;
  /** Activity | Utility | Object — a label only (never affects disposition). */
  type?: string;
  /** review status — 'proposed' | 'confirmed' | 'rejected'. The seam keeps only
   *  'confirmed' (and a no-status legacy fixture is treated as confirmed). */
  status?: string;
  /** 'staff' (default) | 'engine'. */
  source?: string;
  /** the click point in IMAGE-PIXEL space (OQ-C) — the marker anchor (region
   *  centre); re-renders the marker + lets the zones re-derive. Engine ignores. */
  point?: Pt;
  /** the engine's per-zone {disposition, severity, reason, recognised} review
   *  metadata, keyed by zone code. */
  suggestionsByZone?: unknown;
  /** training-signal stamps on a rejected fixture. */
  rejected_by?: string;
  rejected_at?: string;
  /**
   * Module 4 (Phase 2) — the grid id that was active when this fixture was
   * placed. Allows the engine seam to detect if the grid has changed.
   * Optional — old shapes without it remain valid.
   */
  gridId?: string;
}

/** A single floor as the FORM holds it. */
export interface FloorInput {
  /** Stable floor id (e.g. 'f_g', 'f1'). */
  id: string;
  /** Display name, e.g. "Ground floor". */
  name: string;
  /** The principal entry pada (32-pada code). The single main label. */
  main_entry_label?: string;
  /**
   * ENGINE-FACING entrances — one per COVERED pada, DERIVED from `doors[]`.
   * Persisted because the engine reads this; not hand-edited.
   */
  entrances: EntranceInput[];
  /**
   * Stage-3b door markers (door = width-rectangle on the wall). The rich UI
   * shape that re-renders on reload + drives the derived `entrances[]`. Written
   * through THIS form's `saveFloors` (one owner of the floors array).
   */
  doors?: DoorInput[];
  /**
   * Stage-1 grid-first additions (this slice). Written through THIS form's
   * `saveFloors` so the floors array has ONE owner (per the Slice-2
   * merge-at-answers-map invariant — a separate floors write would drop these).
   */
  /** The uploaded floor-plan image (the tracer's raster layer). */
  floorplan?: FloorPlan | null;
  /** The traced boundary polygon in IMAGE-PIXEL space (≥3 pts → grid-able). */
  boundary?: Pt[] | null;
  /**
   * FK → the property grid this floor's boundary belongs to
   * (`properties/{pid}/grids/{gridId}`). Mirrors the optional `gridId?` already
   * carried by `DoorInput`/`FixtureInput`. Stamped only by READ-TIME grid
   * hydration (`hydrateCandidateGrids`) when a candidate floor borrows its linked
   * registry property's traced boundary; the intake save path never writes it.
   * Optional — floors without it remain valid.
   */
  gridId?: string;
  /**
   * Slice-5 AUO fixtures (Activity/Utility/Object marked on the grid → zone
   * derived). Written through THIS form's `saveFloors` so the floors array has
   * ONE owner (riding INSIDE each floor object → the wholesale array merge keeps
   * them, exactly like boundary/doors). The engine reads name+zone; the compose
   * seam strips non-confirmed.
   */
  fixtures?: FixtureInput[];
  /**
   * Eval-canvas MARKUPS (annotations: tick/cross/question/revision-cloud/mirror/
   * arrow/text). Persisted VERBATIM (image-pixel geometry, no engine derivation)
   * and ride INSIDE the floor object so saveFloors round-trips them like doors.
   * Loose structural shape (avoids coupling this core lib to evalMarkups).
   */
  markups?: Array<{
    id: string;
    type: string;
    x: number;
    y: number;
    w?: number;
    h?: number;
    x2?: number;
    y2?: number;
    note?: string;
  }>;
}

/** The staff-technical slice of the intake form. */
export interface StaffIntakeForm {
  /** True-north bearing in degrees [0,360). '' when not yet set. */
  north_rotation: string;
  floors: FloorInput[];
}

/* ─────────────────── MULTI-PROPERTY (L1A — owner ruling D1) ─────────────────
 * One AVBP project = one SELECTION engagement; the intake holds N CANDIDATE
 * PROPERTIES in `answers.properties[]`. Each property carries what is, today,
 * the single property's whole technical intake: its own floors + per-property
 * north bearing + address + site location. The family (`answers.persons`) and
 * the report-context fields (concerns / verdict / room_overrides) are SHARED
 * across all candidates (same family buying).
 *
 * READ-COMPAT SHIM (zero data loss, backfillable): a single-property intake has
 * NO `answers.properties` — it is read as a one-element array
 * `[{ floors: answers.floors, north, address, site }]`. We also keep
 * `answers.floors` written as `properties[0].floors` so the L2B template + the
 * engine's own shim both still see a `floors[]` (L2B is single-property only).
 *
 * The WHOLESALE-array-merge invariant from `saveFloors` carries over verbatim:
 * `answers.properties` is written as one array (Firestore array merge replaces
 * it wholesale; staff own the whole property list each save), riding on the
 * SAME `answers` map as the client keys / site / report-context, so the merge
 * never clobbers a sibling key. ───────────────────────────────────────────── */

/** One candidate property as the FORM holds it. The per-property north is the
 *  owner-facing TRUE bearing string (converted to/from the engine rotation at
 *  the storage boundary, exactly like the top-level `north_rotation`). */
export interface PropertyInput {
  /** Stable property id (e.g. 'prop_a3f1'). */
  id: string;
  /** Registry property id (P#####) once this candidate has been PUSHED to the
   *  registry (AD-048 Step 4). Absent until the first push; stamped back so
   *  subsequent pushes append a NEW version to the SAME registry property
   *  (frozen-snapshot model), not a fresh one. */
  propertyId?: string;
  /** Display label, e.g. "Property A — 12 Oak St". '' allowed. */
  label: string;
  /** The property address printed in §B. '' → derives from site. */
  property_address: string;
  /** Per-property true-north BEARING string (owner-facing). '' when unset →
   *  falls back to the engagement-level north on the engine side. */
  north_rotation: string;
  /** This property's floors (the entire existing per-floor machinery). */
  floors: FloorInput[];
  /**
   * READ-ONLY passthrough of the CLIENT intake's per-property reference media
   * (photos/videos), captured on the client intake form
   * (`intake.ts:PropertyInput.media`). Staff never EDIT this — it simply rides
   * on `answers.properties[i].media` so the registry push (AD-048 step 1b,
   * `candidateRegistryPush.persistIntakeMedia`) can write it through to
   * `properties/{P#####}/media`. Optional; absent on legacy + staff-only
   * candidates. Hydrated by `propertiesFormFrom`; round-tripped (never
   * clobbered) by `propertyToAnswer` so a staff save preserves client media.
   */
  media?: MediaInput[];
  /** This property's site location (coords/link), AV convenience. */
  site_location?: SiteLocation | null;
  /**
   * PER-PROPERTY recommendation (Pass 2 / 2.2). The Acharya's call FOR THIS
   * candidate, set + edited in the per-property evaluation step (AI-draftable).
   * The project-level §D verdict TEXT is a single "Summarize" over all of these.
   * Additive, optional — no schema migration; '' / undefined = not set.
   */
  recommendation_status?: VerdictStatus;
  /** Optional per-property reasoning behind this candidate's recommendation
   *  (staff-edited / AI-drafted). '' / undefined = none. */
  recommendation_reasoning?: string;
}

/** The whole multi-property staff form: N candidate properties + the shared
 *  engagement north (legacy/back-compat top-level bearing). */
export interface PropertiesForm {
  /** Engagement-level true-north bearing (back-compat; used as the per-property
   *  fallback when a property has no own north). '' when unset. */
  north_rotation: string;
  properties: PropertyInput[];
}

/** A blank candidate property (one, no floors yet). */
export function blankProperty(label = ''): PropertyInput {
  return {
    id: newId('prop'),
    label,
    property_address: '',
    north_rotation: '',
    floors: [],
    site_location: null,
  };
}

/**
 * PURE — a NEW candidate slot pre-stamped with a linked registry property
 * (Bug 3 / AD-013). Used when a property is linked (`projects.propertyRefs[]`)
 * but has no candidate to grade yet: staff click an explicit
 * "+ Add candidate for P#####" and this mints the slot. The id is DETERMINISTIC
 * from the propertyId (stable across re-renders / re-clicks for the same ref), and
 * the candidate CARRIES the propertyId so the grid rollup / report can resolve its
 * grid and `buildPropertiesAnswer`'s hasLink filter keeps it. This is an explicit
 * user action — the two-action link/grade model is preserved (never auto-created).
 */
export function candidateForPropertyRef(propertyId: string): PropertyInput {
  return {
    ...blankProperty(),
    id: stableId('prop', `ref:${propertyId}`),
    propertyId,
  };
}

/**
 * REPORT CONTEXT (Slice 7b) — two report-facing fields the staff capture for the
 * L2B report that have NO other source in the data model:
 *
 *   · presenting_concerns — the client's stated problems/goals. The report
 *     template §A reads `answers.presenting_concerns` as an ARRAY (each element a
 *     string, rendered as a bullet). The staff type free multi-line text; one
 *     non-blank line = one concern bullet (`textToConcerns` splits on newlines).
 *   · property_address — the site address printed on the cover. When the staff
 *     leave it blank, the report DERIVES a fallback from `answers.site_location`
 *     (functions side) — so a blank here is fine, not a gap.
 *
 * Both ride on the SAME intake doc `clients/{id}/intake/L2B_R1.answers` as the
 * client keys + the staff floors/site_location, and are MERGE-written so they
 * never clobber `floors` / `site_location` / the client-owned `answers.*` keys.
 * The form holds the concerns as raw textarea TEXT (one line per concern); the
 * pure `textToConcerns` / `concernsToText` convert at the storage boundary.
 */
export interface ReportContextForm {
  /** Raw multi-line text — one non-blank line per presenting concern. */
  presenting_concerns_text: string;
  /** The property address printed on the cover. '' → cover derives from site. */
  property_address: string;
  /**
   * VERDICT (curation) — the Vastu Acharya's overall recommendation, typed at
   * report review. Renders inside L1A §D ("Verdict & Recommendation"). Blank →
   * the §D curation frame ("to be completed at curation") stays.
   *   · verdict_text — the headline recommendation (1–3 lines).
   *   · verdict_reasoning_text — the longer cross-lens (Vastu · Astro-Vastu ·
   *     remedy-load) reasoning, multi-line (one paragraph per non-blank line).
   *   · verdict_status — optional chip; '' = no chip. One of:
   *       'recommended' | 'recommended_with_remedies' | 'not_recommended'.
   */
  verdict_text: string;
  verdict_reasoning_text: string;
  verdict_status: VerdictStatus;
  /**
   * ROOM ALLOCATION (curation) — the Acharya's override of who sleeps where,
   * overriding the engine's Astro-Vastu draft allocation (L1A §E/§F). Each row
   * maps a member (or the literal "couple" for the married pair) to a marked
   * bedroom (zone + optional floor). Persisted as `answers.room_overrides`
   * keyed by the member label; the functions reconciler reads it and the
   * override WINS over the engine default (OQ-5 confirm-loop). Empty list →
   * no override, the engine's couple-grouped default renders.
   */
  room_overrides: RoomOverrideRow[];
}

/** One room-allocation override row (UI shape). `member` = the label/key (a
 *  member name, an astro person id, or the literal "couple"); `zone` = one of
 *  the 10 Vastu-valid bedroom zones; `floorId` = optional marked-floor id. */
export interface RoomOverrideRow {
  member: string;
  zone: string;
  floorId: string;
}

/** The 10 Vastu-valid bedroom zones (Set-9) — the override zone choices. Mirrors
 *  VALID_BEDROOM_ZONES in functions/src/composer/render/astroAllocation.js. */
export const VALID_BEDROOM_ZONES: readonly string[] = [
  'ENE', 'East', 'SSE', 'South', 'SW', 'WSW', 'West', 'NW', 'NNW', 'North',
];

/** The optional recommendation chip on §D. '' = no chip shown. */
export type VerdictStatus =
  | ''
  | 'recommended'
  | 'recommended_with_remedies'
  | 'not_recommended';

/** PURE — coerce an arbitrary stored value to a valid VerdictStatus (or ''). */
export function normaliseVerdictStatus(v: unknown): VerdictStatus {
  return v === 'recommended' ||
    v === 'recommended_with_remedies' ||
    v === 'not_recommended'
    ? v
    : '';
}

/** PURE — coerce an arbitrary stored value to a valid bedroom-zone label (or ''). */
export function normaliseBedroomZone(v: unknown): string {
  const z = typeof v === 'string' ? v.trim() : '';
  // accept the short compass codes the engine emits too (E/N/S/W → label).
  const map: Record<string, string> = { E: 'East', N: 'North', S: 'South', W: 'West' };
  const canon = map[z] || z;
  return VALID_BEDROOM_ZONES.includes(canon) ? canon : '';
}

/** PURE — hydrate the room-override rows from a stored `answers.room_overrides`
 *  map ({ key: { zone, floorId } }). Drops entries with no valid zone. Stable
 *  order: the literal "couple" first, then alphabetical by member key. */
export function roomOverridesFrom(v: unknown): RoomOverrideRow[] {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return [];
  const rows: RoomOverrideRow[] = [];
  for (const [k, raw] of Object.entries(v as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue;
    const o = raw as { zone?: unknown; floorId?: unknown; floor_id?: unknown };
    const zone = normaliseBedroomZone(o.zone);
    if (!zone) continue;
    const floorId =
      typeof o.floorId === 'string' ? o.floorId
        : typeof o.floor_id === 'string' ? o.floor_id : '';
    rows.push({ member: String(k).trim(), zone, floorId });
  }
  rows.sort((a, b) => {
    const ca = a.member.toLowerCase() === 'couple' ? 0 : 1;
    const cb = b.member.toLowerCase() === 'couple' ? 0 : 1;
    return ca - cb || a.member.localeCompare(b.member);
  });
  return rows;
}

/** PURE — build the stored `room_overrides` map from the UI rows. Drops rows with
 *  a blank member key or an invalid zone; last write wins on a duplicate key.
 *  Returns `{}` when there are no valid rows (a wholesale-map merge then clears
 *  any previously-stored overrides). */
export function roomOverridesToAnswer(
  rows: RoomOverrideRow[] | null | undefined,
): Record<string, { zone: string; floorId: string }> {
  const out: Record<string, { zone: string; floorId: string }> = {};
  for (const r of rows || []) {
    const key = (r.member || '').trim();
    const zone = normaliseBedroomZone(r.zone);
    if (!key || !zone) continue;
    out[key] = { zone, floorId: (r.floorId || '').trim() };
  }
  return out;
}

/** PURE — split the textarea into the stored `presenting_concerns` ARRAY: one
 *  trimmed, non-empty line per concern (the shape the report template §A reads,
 *  rendering each element as a bullet). Returns [] for blank/whitespace input. */
export function textToConcerns(text: unknown): string[] {
  if (typeof text !== 'string') return [];
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

/** PURE — the inverse: a stored `presenting_concerns` value (array of strings or
 *  `{text}` objects, or a single string) → the editable textarea text (one
 *  concern per line). Tolerant of the legacy/object element shapes the template
 *  also accepts. Returns '' when absent. */
export function concernsToText(v: unknown): string {
  if (typeof v === 'string') return v.trim();
  if (!Array.isArray(v)) return '';
  return v
    .map((c) => {
      if (typeof c === 'string') return c.trim();
      if (c && typeof c === 'object' && typeof (c as { text?: unknown }).text === 'string') {
        return ((c as { text: string }).text || '').trim();
      }
      return '';
    })
    .filter((l) => l.length > 0)
    .join('\n');
}

/** A blank report-context form. */
export function blankReportContext(): ReportContextForm {
  return {
    presenting_concerns_text: '',
    property_address: '',
    verdict_text: '',
    verdict_reasoning_text: '',
    verdict_status: '',
    room_overrides: [],
  };
}

/** PURE — hydrate the report-context form from an existing intake doc's
 *  `answers` (defensive across a partial/absent doc). */
export function reportContextFrom(
  answers: Record<string, unknown> | null | undefined,
): ReportContextForm {
  const a = answers || {};
  return {
    presenting_concerns_text: concernsToText(a.presenting_concerns),
    property_address:
      typeof a.property_address === 'string' ? a.property_address.trim() : '',
    // Verdict (curation) — same `answers` map; round-trips on reload.
    verdict_text:
      typeof a.verdict === 'string' ? a.verdict.trim() : '',
    verdict_reasoning_text:
      typeof a.verdict_reasoning === 'string' ? a.verdict_reasoning.trim() : '',
    verdict_status: normaliseVerdictStatus(a.verdict_status),
    // Room-allocation overrides (curation) — same `answers` map; round-trip.
    room_overrides: roomOverridesFrom(a.room_overrides),
  };
}

/** PURE — build the `answers.*` patch from the report-context form.
 *  `presenting_concerns` is ALWAYS emitted as an array (possibly empty — a
 *  wholesale array merge replaces the old value, so clearing all concerns
 *  persists `[]`, not an orphaned old list); `property_address` / `verdict` /
 *  `verdict_reasoning` as trimmed strings (possibly ''); `verdict_status` as a
 *  validated enum string (possibly ''). Stable, predictable shape for the merge
 *  write — these keys never clobber `floors` / `site_location` / the
 *  client-owned `answers.*` keys (merge is at the `answers` map level). */
export function buildReportContextAnswer(
  form: ReportContextForm,
): {
  presenting_concerns: string[];
  property_address: string;
  verdict: string;
  verdict_reasoning: string;
  verdict_status: VerdictStatus;
  room_overrides: Record<string, { zone: string; floorId: string }>;
} {
  return {
    presenting_concerns: textToConcerns(form.presenting_concerns_text),
    property_address: (form.property_address || '').trim(),
    verdict: (form.verdict_text || '').trim(),
    verdict_reasoning: (form.verdict_reasoning_text || '').trim(),
    verdict_status: normaliseVerdictStatus(form.verdict_status),
    room_overrides: roomOverridesToAnswer(form.room_overrides),
  };
}

/* ──────────────────────────── pure helpers ───────────────────────────── */

/**
 * Default internal-door segment length in IMAGE PIXELS — used when MIGRATING a
 * legacy point internal door (`at`) to the rectangular segment model so it
 * renders as a width-door. Mirrors `DEFAULT_DOOR_WIDTH_PX` in entranceMarks.ts;
 * duplicated here (a small literal) to avoid a circular import (entranceMarks
 * already imports this module). One number, easy to reconcile.
 */
export const INTERNAL_DOOR_WIDTH_PX = 56;

/** A stable-enough id for a new floor/entrance row (form-local). */
export function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * A DETERMINISTIC form-local id from a stable `seed` (same seed → same id),
 * shaped like `newId` (`prefix_xxxxxx`). Used so a value minted from a stable
 * scope (e.g. the read-compat synthesised candidate, or a candidate created for a
 * linked property ref) keeps a CONSTANT id across loads — a transient empty
 * `answers.properties` can never churn the candidate identity, so the
 * `recommended_property_id` FK (matched by candidate id) can't be silently
 * invalidated (Bug 2). FNV-1a 32-bit → base36, fixed 6-char width.
 */
export function stableId(prefix: string, seed: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const s = (h >>> 0).toString(36).padStart(6, '0').slice(-6);
  return `${prefix}_${s}`;
}

/** A blank floor row (one empty entrance). */
export function blankFloor(name = ''): FloorInput {
  return {
    id: newId('f'),
    name,
    main_entry_label: '',
    entrances: [],
    doors: [],
    floorplan: null,
    boundary: null,
    fixtures: [],
  };
}

/* ─────────────────── AUO fixtures (Slice 5 — confirm-loop) ─────────────────── */

/** PURE — map ONE form fixture → its persisted `fixtures[]` shape. Keeps the
 *  full review metadata (status/source/suggestion/point) so the staff list
 *  round-trips on reload; the COMPOSE SEAM strips non-confirmed + metadata
 *  before the engine.
 *
 *  ⭐ BUG FIX (2026-06-08): keep a placeable marker (a valid POINT) even when it
 *  is still NAMELESS — a freshly-placed AUO has `name: ''` until the staff names
 *  it, and it must survive a save→reload as a still-to-be-named card (mirrors the
 *  in-session `markToFixture` fix). A nameless 'proposed' fixture never reaches
 *  the engine (the compose seam keeps only 'confirmed', and Confirm needs a
 *  name). Still drops a degenerate fixture with NEITHER a renderable point NOR an
 *  analysable name+zone. */
/** True when a point carries a renderable {x,y} in finite image-pixel space.
 *  Shared by `fixtureToAnswer` (the serializer keep-rule) and `buildFloorsAnswer`
 *  (the floor-level keep gate) so both agree on what makes a fixture renderable —
 *  a freshly-marked, still-nameless AUO is kept alive by its point alone. */
export function hasFinitePoint(pt: Pt | null | undefined): pt is Pt {
  return (
    !!pt &&
    typeof pt.x === 'number' &&
    typeof pt.y === 'number' &&
    Number.isFinite(pt.x) &&
    Number.isFinite(pt.y)
  );
}

/** The canonical fixture keep-rule (mirrors `fixtureToAnswer`'s null-return): keep
 *  a fixture that has a renderable point OR (a name + at least one valid zone).
 *  Used by the floor-level gate so an AUO-only floor is never dropped. */
export function fixtureIsKeepable(fx: FixtureInput): boolean {
  const name = (fx.name || '').trim();
  const zone = (fx.zone || '').trim().toUpperCase();
  const validZone = isZoneCode(zone);
  const hasZones =
    Array.isArray(fx.zones) && fx.zones.some((z) => isZoneCode(z));
  return hasFinitePoint(fx.point) || (!!name && (validZone || hasZones));
}

export function fixtureToAnswer(fx: FixtureInput): Record<string, unknown> | null {
  const name = (fx.name || '').trim();
  const zone = (fx.zone || '').trim().toUpperCase();
  const validZone = isZoneCode(zone);
  const pt = fx.point;
  const hasPoint = hasFinitePoint(pt);
  const zonesClean = Array.isArray(fx.zones)
    ? Array.from(
        new Set(
          fx.zones
            .map((z) => (typeof z === 'string' ? z.trim().toUpperCase() : ''))
            .filter((z) => isZoneCode(z)),
        ),
      )
    : [];
  // keep a renderable marker (has a point) OR an analysable fixture (name + ≥1 zone).
  if (!hasPoint && (!name || (!validZone && zonesClean.length === 0))) return null;
  const out: Record<string, unknown> = {
    name,
    zone: validZone ? zone : zonesClean[0] || '',
  };
  if (zonesClean.length) out.zones = zonesClean;
  const type = trimOrUndef(fx.type);
  if (type) out.type = type;
  const status = trimOrUndef(fx.status);
  if (status) out.status = status;
  const source = trimOrUndef(fx.source);
  if (source) out.source = source;
  if (hasPoint && pt) {
    out.point = { x: Math.round(pt.x), y: Math.round(pt.y) };
  }
  if (fx.region && typeof fx.region === 'object') out.region = fx.region;
  if (fx.suggestionsByZone && typeof fx.suggestionsByZone === 'object') {
    out.suggestionsByZone = fx.suggestionsByZone;
  }
  if (fx.status === 'rejected') {
    const by = trimOrUndef(fx.rejected_by);
    if (by) out.rejected_by = by;
    const at = trimOrUndef(fx.rejected_at);
    if (at) out.rejected_at = at;
  }
  return out;
}

/** The 16 valid zone codes (compass) — a fixture's `zone` must be one of these
 *  (derived client-side via zoneAtPoint). Inlined (not imported from vastuGrid)
 *  to keep this pure module free of the grid lib; the set is fixed + canonical. */
export const ZONE_CODES = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
] as const;
export function isZoneCode(s: unknown): boolean {
  return (
    typeof s === 'string' &&
    (ZONE_CODES as readonly string[]).includes(s.trim().toUpperCase())
  );
}

/** PURE — a boundary is grid-able when it has ≥3 finite {x,y} points. */
export function isUsableBoundary(b: unknown): b is Pt[] {
  if (!Array.isArray(b) || b.length < 3) return false;
  return b.every(
    (p) =>
      p &&
      typeof (p as Pt).x === 'number' &&
      typeof (p as Pt).y === 'number' &&
      Number.isFinite((p as Pt).x) &&
      Number.isFinite((p as Pt).y),
  );
}

/* ─────────────────── boundary cleaning (single source) ─────────────────── */

/**
 * PURE — clean a traced boundary ONCE and re-map any doors against the cleaned
 * polygon, so the live grid is byte-identical in SHAPE to the locked v7 preview.
 *
 * WHY (Slice-3 bug 2 — geometry drift): the v7 preview cleans the RAW traced
 * polygon ONCE (`cleanBoundary` — strip <3px dupes, drop the coincident close,
 * force CW winding) and computes EVERYTHING (centroid / 32-ray L / zone strip /
 * labels) from THAT cleaned polygon. The live tool previously cleaned the
 * OVERLAY only while the editable / persisted `pts` and the engine grid still
 * used the RAW polygon (with the S3 1px duplicate) → the red overlay boundary
 * (cleaned) and the centroid/grid (raw) disagreed with the preview. Cleaning at
 * the SINGLE UI↔storage boundary makes the editable vertices, the grid math, the
 * overlay, AND what's persisted all use ONE cleaned polygon — no drift.
 *
 * DOOR SAFETY: doors store `edge_index` + `parametric_t` against the boundary.
 * Cleaning can DROP a vertex (dedupe / close-coincident) AND REVERSE the winding
 * (CCW→CW) — both shift edge indices. Rather than fragile index arithmetic, we
 * RECOMPUTE each door from its stored geometry: reconstruct the door's centre as
 * an absolute {x,y} on the RAW boundary, then re-snap it to the CLEANED boundary
 * (`nearestEdge`) → a fresh `edge_index`/`parametric_t`. Width is preserved. A
 * door therefore stays on its physical wall through dedupe AND reversal. Doors
 * with an unplaceable centre are dropped (their wall vanished).
 *
 * Idempotent: an already-clean boundary returns an equivalent boundary + doors.
 * A boundary with <3 usable points is returned untouched (nothing to clean).
 */
export function cleanFloorBoundary(
  rawBoundary: Pt[] | null | undefined,
  doors: DoorInput[] | null | undefined,
): { boundary: Pt[] | null; doors: DoorInput[] } {
  const safeDoors = Array.isArray(doors) ? doors : [];
  if (!isUsableBoundary(rawBoundary)) {
    return { boundary: rawBoundary ?? null, doors: safeDoors };
  }
  const raw = rawBoundary as Pt[];
  const cleaned = cleanBoundary(raw);
  if (cleaned.length < 3) {
    // degenerate — don't replace a usable polygon with an unusable one.
    return { boundary: raw, doors: safeDoors };
  }
  const remapped: DoorInput[] = [];
  for (const d of safeDoors) {
    if (!d) continue;
    // INTERNAL doors store ABSOLUTE interior points (segment `a`/`b`, or a legacy
    // `at`) in image-pixel space — independent of the boundary's vertex set /
    // winding — so cleaning the polygon never moves them; pass through unchanged.
    if (d.placement === 'internal') {
      if (hasInternalGeometry(d)) remapped.push(d);
      continue;
    }
    // PERIMETER doors store edge_index + parametric_t against the boundary;
    // cleaning can drop a vertex AND reverse the winding → re-snap by physical
    // {x,y} (handles dedupe AND the CCW→CW reversal). Width/metadata carry over.
    if (!hasFiniteEdge(d)) continue;
    const centre = edgePointAt(raw, d.edge_index as number, d.parametric_t as number);
    if (!centre) continue;
    const hit = nearestEdge(centre, cleaned);
    if (!hit) continue;
    remapped.push({
      ...d,
      edge_index: hit.edge_index,
      parametric_t: +hit.parametric_t.toFixed(4),
    });
  }
  return { boundary: cleaned, doors: remapped };
}

/** The blank staff form (no floors yet). */
export function blankStaffForm(): StaffIntakeForm {
  return { north_rotation: '', floors: [] };
}

/**
 * PURE — validate + normalise a north bearing to a finite number in [0,360),
 * or null. Accepts a string or number; 360 wraps to 0; out-of-range / NaN →
 * null. The engine reads it via Number.isFinite, so we must never write NaN.
 */
export function normaliseNorth(v: unknown): number | null {
  let n: number;
  if (typeof v === 'number') n = v;
  else {
    const s = typeof v === 'string' ? v.trim() : '';
    if (!s) return null;
    n = Number(s);
  }
  if (!Number.isFinite(n)) return null;
  if (n < 0 || n > 360) return null;
  // 360 === 0 (a full turn).
  return n === 360 ? 0 : n;
}

/**
 * PURE — the bearing ↔ engine-rotation conversion. ONE consistent convention,
 * applied at the SINGLE UI↔storage boundary (no engine change, no fixture regen).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (owner correction, locked 2026-06-07)
 * ─────────────────────────────────────────────────────────────────────────
 *  The grid engine (`lib/vastu-grid` + the portal port `vastuGrid.ts`) renders
 *  the grid's North arrow at SCREEN-bearing equal to the stored `north_rotation`
 *  (verified empirically: rayDir(deg)=(sin,-cos); compass-North renders at
 *  rayDir(rotation), so north_rotation=R → grid-North points to screen-bearing
 *  R° clockwise-from-up). The owner's surveyed TRUE-north bearing runs in the
 *  OPPOSITE rotational sense — the owner reported "whatever degree I put in, I
 *  have to subtract it from 360 to get the right direction." So the value that
 *  orients the grid CORRECTLY is `(360 − bearing)`.
 *
 *  Fix: the owner types their TRUE bearing; we store `north_rotation =
 *  (360 − bearing) mod 360` (the rotation the engine actually needs) and display
 *  `bearing = (360 − north_rotation) mod 360`. The conversion is self-inverse, so
 *  one function serves both directions. The portal tracer is given the SAME
 *  stored `north_rotation` the engine reads (passed straight to `computeGrid`),
 *  so portal grid == engine grid with no drift.
 *
 *  NOTE: pre-prod test data persisted north values in the OLD raw-bearing
 *  convention; those will now read back inverted. Acceptable (no live clients,
 *  no migration) — call it out at handoff.
 *
 *  Returns null for an invalid input (mirrors normaliseNorth), so a junk bearing
 *  never writes NaN.
 */
export function bearingToRotation(bearing: unknown): number | null {
  const b = normaliseNorth(bearing);
  if (b == null) return null;
  // Round to 1 dp so the stored rotation carries no float artifact (the `360 −
  // bearing` subtraction can leave a long tail, e.g. 304.20000000000005, which
  // read back is the `55.7999…` bug). One tenth of a degree is finer than any
  // surveyed north needs. (16th dispatch.)
  return roundBearing(((360 - b) % 360 + 360) % 360);
}

/** PURE — the inverse: stored engine-rotation → the owner-facing TRUE bearing.
 *  Self-inverse with `bearingToRotation` (R ↔ B both = 360−other mod 360).
 *
 *  ROUNDING (2026-06-08, 16th dispatch): the grid math derives the rotation as
 *  `(360 − bearing)` in floating point, so a stored rotation can carry a float
 *  artifact (e.g. 304.20000000000005) that read back as a bearing reads
 *  `55.79999999999955`. The owner-facing bearing never needs sub-degree
 *  precision finer than a tenth, so we round to 1 dp — `55.8`, not `55.7999…`.
 *  `roundBearing` is exported so the SAVE path can persist the clean value too
 *  (display + stored both read clean). */
export function roundBearing(n: number): number {
  return Math.round(n * 10) / 10;
}
export function rotationToBearing(rotation: unknown): number | null {
  const r = normaliseNorth(rotation);
  if (r == null) return null;
  return roundBearing(((360 - r) % 360 + 360) % 360);
}

function trimOrUndef(v: unknown): string | undefined {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s : undefined;
}

/**
 * PURE — map ONE form entrance → the engine's `entrances[]` shape. Drops a
 * blank/invalid code (the engine matches on the pada code). Returns null when
 * the label is not a valid code, so the caller filters it out.
 */
export function entranceToAnswer(
  e: EntranceInput,
): { label: string; name?: string; type?: string; point?: Pt } | null {
  const code = (e.label || '').trim().toUpperCase();
  if (!isEntranceCode(code)) return null;
  const out: { label: string; name?: string; type?: string; point?: Pt } = {
    label: code,
  };
  const name = trimOrUndef(e.name);
  if (name) out.name = name;
  const type = trimOrUndef(e.type);
  if (type) out.type = type;
  // Stage-3: carry the click point (image-pixel space) so the on-map marker
  // re-renders on reload. Only when both coords are finite (rounded to ints).
  const pt = e.point;
  if (
    pt &&
    typeof pt.x === 'number' &&
    typeof pt.y === 'number' &&
    Number.isFinite(pt.x) &&
    Number.isFinite(pt.y)
  ) {
    out.point = { x: Math.round(pt.x), y: Math.round(pt.y) };
  }
  return out;
}

/** True when a point is a finite {x,y}. */
function isFinitePt(p: unknown): p is Pt {
  return !!(
    p &&
    typeof (p as Pt).x === 'number' &&
    typeof (p as Pt).y === 'number' &&
    Number.isFinite((p as Pt).x) &&
    Number.isFinite((p as Pt).y)
  );
}

/** True when a door's LEGACY interior point `at` is a finite {x,y}. */
export function hasFiniteAt(d: DoorInput): d is DoorInput & { at: Pt } {
  return isFinitePt(d.at);
}

/** True when an internal door's segment endpoints `a`/`b` are both finite. */
export function hasFiniteSegment(
  d: DoorInput,
): d is DoorInput & { a: Pt; b: Pt } {
  return isFinitePt(d.a) && isFinitePt(d.b);
}

/** True when an internal door is placeable — a finite segment OR a legacy point. */
export function hasInternalGeometry(d: DoorInput): boolean {
  return hasFiniteSegment(d) || hasFiniteAt(d);
}

/** True when a door's edge_index + parametric_t are finite (perimeter-placeable). */
export function hasFiniteEdge(
  d: DoorInput,
): d is DoorInput & { edge_index: number; parametric_t: number } {
  return (
    typeof d.edge_index === 'number' &&
    Number.isFinite(d.edge_index) &&
    typeof d.parametric_t === 'number' &&
    Number.isFinite(d.parametric_t)
  );
}

/**
 * PURE — map ONE form door → the persisted `doors[]` shape. A PERIMETER door
 * emits edge/slide/width; an INTERNAL door (placement==='internal') emits the
 * interior point `at` instead. Drops a door that is neither placeable
 * (no finite edge AND no finite `at`). The engine ignores this array; it
 * re-renders the marker on reload and drives `entrances[]`.
 */
export function doorToAnswer(
  d: DoorInput,
): Record<string, unknown> | null {
  const internal = d.placement === 'internal';
  const out: Record<string, unknown> = { principal: d.principal === true };
  if (internal) {
    out.placement = 'internal';
    if (hasFiniteSegment(d)) {
      // segment model: two free endpoints + width (rectangular, rotatable).
      out.a = { x: Math.round(d.a.x), y: Math.round(d.a.y) };
      out.b = { x: Math.round(d.b.x), y: Math.round(d.b.y) };
      out.width_image_px =
        typeof d.width_image_px === 'number' && Number.isFinite(d.width_image_px)
          ? +d.width_image_px.toFixed(2)
          : 0;
    } else if (hasFiniteAt(d)) {
      // LEGACY point internal door — keep `at` so an old doc round-trips until
      // re-marked. The loader migrates it to a tiny segment.
      out.at = { x: Math.round(d.at.x), y: Math.round(d.at.y) };
    } else {
      return null;
    }
  } else {
    if (!hasFiniteEdge(d)) return null;
    out.placement = 'perimeter';
    out.edge_index = Math.round(d.edge_index as number);
    out.parametric_t = +(d.parametric_t as number).toFixed(4);
    out.width_image_px =
      typeof d.width_image_px === 'number' && Number.isFinite(d.width_image_px)
        ? +d.width_image_px.toFixed(2)
        : 0;
  }
  const type = trimOrUndef(d.type);
  if (type) out.type = type;
  const name = trimOrUndef(d.name);
  if (name) out.name = name;
  return out;
}

/**
 * PURE — map ONE form floor → the engine's `answers.floors[i]` shape. Emits
 * id + name always; `main_entry_label` only when a valid pada code; `entrances`
 * (the DERIVED one-per-covered-pada list) filtered to valid codes; the rich
 * `doors[]` markers (Stage 3b, re-render on reload — engine ignores them); and
 * the Slice-5 `fixtures[]` (AUO marks → {name, zone} + review metadata).
 */
export function floorToAnswer(f: FloorInput): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: f.id || newId('f'),
    name: (f.name || '').trim(),
  };
  const main = (f.main_entry_label || '').trim().toUpperCase();
  if (isEntranceCode(main)) out.main_entry_label = main;
  const entrances = (f.entrances || [])
    .map(entranceToAnswer)
    .filter((x): x is NonNullable<typeof x> => x != null);
  out.entrances = entrances;
  // Bug-2 guarantee: PERSIST the CLEANED boundary (and doors re-mapped onto it),
  // so the stored polygon is byte-identical in shape to the v7 preview — the
  // editable form is already cleaned (panel cleans on load + on edit), so this
  // is idempotent, but it pins the contract at the single UI↔storage boundary so
  // no raw 1px-duplicate ever reaches the engine or a future reload.
  const { boundary: cleanedB, doors: cleanedDoors } = cleanFloorBoundary(
    f.boundary,
    f.doors,
  );
  // Rich door markers (Stage 3b). Always emit the array (possibly empty) so a
  // floor that had its doors cleared persists the empty list (wholesale array
  // merge → the old doors are replaced, not orphaned).
  out.doors = cleanedDoors
    .map(doorToAnswer)
    .filter((x): x is NonNullable<typeof x> => x != null);
  // Slice-5 AUO fixtures (the confirm-loop). Always emit the array (possibly
  // empty) so a floor whose fixtures were all removed persists the empty list
  // (wholesale array merge → the old fixtures are replaced, not orphaned). Each
  // fixture rides INSIDE the floor object → the one-owner saveFloors keeps it.
  out.fixtures = (f.fixtures || [])
    .map(fixtureToAnswer)
    .filter((x): x is NonNullable<typeof x> => x != null);
  // Stage-1 grid-first fields. The engine reads `boundary` at
  // ensureGrids.js:45-49 (≥3 {x,y} pts → computeGrid, else the floor is
  // skipped). `floorplan` is intake/UI metadata (the engine ignores it). Both
  // are emitted only when present so a scaffold-only floor stays clean.
  if (isUsableBoundary(cleanedB)) {
    out.boundary = (cleanedB as Pt[]).map((p) => ({ x: p.x, y: p.y }));
  }
  if (f.floorplan && (f.floorplan.downloadURL || f.floorplan.storagePath)) {
    out.floorplan = floorPlanDocBody(f.floorplan);
  }
  // Eval-canvas MARKUPS (annotations). Persisted VERBATIM (no derivation) so they
  // round-trip like doors/fixtures — they ride INSIDE the floor object so the
  // one-owner saveFloors keeps them. Always emit the array (possibly empty) so a
  // floor whose markups were all removed persists the empty list (wholesale
  // array merge → old markups are replaced, not orphaned).
  out.markups = (f.markups || []).map((m) => ({ ...m }));
  return out;
}

/**
 * PURE — the staff floor scaffold → the `answers.floors[]` array exactly as
 * `clients/{id}/intake/L2B_R1.answers.floors`. Drops fully-blank floors (no
 * name, no main label, no entrances).
 */
export function buildFloorsAnswer(
  form: StaffIntakeForm,
): Record<string, unknown>[] {
  return (form.floors || [])
    .filter((f) => {
      const named = !!(f.name || '').trim();
      const hasMain = isEntranceCode((f.main_entry_label || '').trim().toUpperCase());
      const hasEntr = (f.entrances || []).some((e) =>
        isEntranceCode((e.label || '').trim().toUpperCase()),
      );
      const hasDoor = (f.doors || []).some(
        (d) => hasFiniteEdge(d) || hasInternalGeometry(d),
      );
      const hasBoundary = isUsableBoundary(f.boundary);
      const hasPlan = !!(
        f.floorplan &&
        (f.floorplan.downloadURL || f.floorplan.storagePath)
      );
      // Keep a floor whose ONLY content is a fixture that fixtureToAnswer would
      // itself serialize — i.e. a renderable point OR name+zone. A freshly-marked
      // AUO is nameless until staff pick a palette label, so gating on name+zone
      // (as before) dropped the whole floor → floors:[] data loss. Mirror the
      // serializer's keep-rule via fixtureIsKeepable.
      const hasFixture = (f.fixtures || []).some(fixtureIsKeepable);
      // Keep a floor whose ONLY content is a markup (annotation). Markups ride
      // inside the floor object and round-trip via floorToAnswer; without this
      // term a markup-only floor was silently filtered out.
      const hasMarkup = Array.isArray(f.markups) && f.markups.length > 0;
      return (
        named || hasMain || hasEntr || hasDoor || hasBoundary || hasPlan || hasFixture || hasMarkup
      );
    })
    .map(floorToAnswer);
}

/* ─────────────────── MULTI-PROPERTY build / hydrate (pure) ─────────────────── */

/**
 * PURE — map ONE form property → its persisted `answers.properties[i]` shape:
 *   { id, label, property_address, north_rotation (ENGINE rotation), site_location, floors }
 * `floors` reuses `buildFloorsAnswer` (one-owner-of-the-floors-array contract,
 * per property). `north_rotation` is converted bearing→engine-rotation at the
 * storage boundary (mirrors `saveNorthRotation`); '' / invalid → omitted so the
 * engine falls back to the engagement north. Drops nothing structurally — a
 * property with no floors persists as `floors: []` (wholesale array replace).
 */
export function propertyToAnswer(p: PropertyInput): Record<string, unknown> {
  const floors = buildFloorsAnswer({
    north_rotation: '',
    floors: p.floors || [],
  });
  const out: Record<string, unknown> = {
    id: p.id || newId('prop'),
    label: (p.label || '').trim(),
    property_address: (p.property_address || '').trim(),
    floors,
  };
  // Registry link (AD-048 Step 4) — persisted so pushes reuse the same P#####.
  if (typeof p.propertyId === 'string' && p.propertyId) out.propertyId = p.propertyId;
  // Round-trip the client intake media (step 1b feeder source) so a STAFF save
  // (wholesale `answers.properties` array replace) NEVER clobbers it. Only the
  // client write path mints these; staff treat them as read-only passthrough.
  if (Array.isArray(p.media) && p.media.length) out.media = p.media;
  // Pass 2 / 2.2 — per-property recommendation (additive; only persisted when set).
  const rec = normaliseVerdictStatus(p.recommendation_status);
  if (rec) out.recommendation_status = rec;
  const recReason =
    typeof p.recommendation_reasoning === 'string' ? p.recommendation_reasoning.trim() : '';
  if (recReason) out.recommendation_reasoning = recReason;
  const rot = bearingToRotation(p.north_rotation);
  if (rot != null) out.north_rotation = rot;
  if (p.site_location && typeof p.site_location === 'object') {
    out.site_location = p.site_location;
  }
  return out;
}

/**
 * PURE — the multi-property form → the `answers.properties[]` array. Drops a
 * property that is entirely blank (no label, no address, no floors, no registry
 * link (`propertyId`)). Always returns at least the properties the staff
 * defined; an empty list is valid (the caller then writes a 1-element array or
 * clears the key).
 */
export function buildPropertiesAnswer(
  form: PropertiesForm,
): Record<string, unknown>[] {
  return (form.properties || [])
    .filter((p) => {
      const hasLabel = !!(p.label || '').trim();
      const hasAddr = !!(p.property_address || '').trim();
      const hasFloors = buildFloorsAnswer({ north_rotation: '', floors: p.floors || [] }).length > 0;
      const hasLink = !!(p.propertyId || '').trim(); // a registry-linked candidate is meaningful — keep it so the FK persists (L1A connector)
      return hasLabel || hasAddr || hasFloors || hasLink;
    })
    .map(propertyToAnswer);
}

/**
 * PURE — hydrate the multi-property form from an existing intake doc's
 * `answers`. READ-COMPAT: when `answers.properties` is absent/empty, synthesise
 * a ONE-element array from the legacy single-property fields
 * (`answers.floors` + the engagement north + `answers.property_address` +
 * `answers.site_location`). Each property's floors hydrate via `staffFormFrom`
 * (so all the per-floor machinery — boundary/doors/fixtures — round-trips).
 *
 * @param answers the intake `answers` map
 * @param north   the STORED engagement engine-rotation (profile.north_rotation)
 */
export function propertiesFormFrom(
  answers: Record<string, unknown> | null | undefined,
  north: unknown,
  scope?: { clientId?: string; projectId?: string },
): PropertiesForm {
  const a = answers || {};
  const engagementBearing = rotationToBearing(north);
  const engagementNorthStr = engagementBearing == null ? '' : String(engagementBearing);

  // EXPLICIT-EMPTY IS AUTHORITATIVE (owner bug 2026-07-03, "remove the last
  // candidate must survive refresh"): a PRESENT `answers.properties` array —
  // even empty — is a post-migration doc whose staff own the whole candidate
  // list (`saveProperties` array-replaces it and mirrors `answers.floors` in
  // the same write). Synthesising a legacy one-element candidate from the
  // mirror fields here would RESURRECT a deliberately removed last candidate
  // on every reload. The legacy READ-COMPAT shim below now fires only when the
  // key is genuinely ABSENT (a pre-multi-property doc). Safe: the only writer
  // of `properties: []` is `saveProperties`, which zeroes the `floors` mirror
  // in the same write (the floors-only `saveFloors` writer has no live caller).
  const rawProps = Array.isArray(a.properties) ? a.properties : null;
  let properties: PropertyInput[];

  if (rawProps) {
    properties = rawProps.map((pr) => {
      const po = (pr || {}) as Record<string, unknown>;
      // each property carries its OWN floors + north + address + site.
      const sub = staffFormFrom(po as Record<string, unknown>, po.north_rotation);
      const site = siteLocationFromAnswer(po.site_location);
      return {
        id: typeof po.id === 'string' && po.id ? po.id : newId('prop'),
        ...(typeof po.propertyId === 'string' && po.propertyId
          ? { propertyId: po.propertyId }
          : {}),
        label: typeof po.label === 'string' ? po.label : '',
        property_address:
          typeof po.property_address === 'string' ? po.property_address.trim() : '',
        // per-property north hydrates from the property's own rotation; '' → the
        // engagement north is the engine fallback (we leave the field blank).
        north_rotation: (() => {
          const b = rotationToBearing(po.north_rotation);
          return b == null ? '' : String(b);
        })(),
        floors: sub.floors,
        // READ-ONLY passthrough of the client intake media (step 1b feeder).
        // Carried verbatim from the stored row; staff never edit it. Firestore
        // never returns `undefined` keys, so the round-trip stays write-safe.
        ...(Array.isArray(po.media) ? { media: po.media as MediaInput[] } : {}),
        site_location: site,
        // Pass 2 / 2.2 — per-property recommendation round-trips off the stored row.
        recommendation_status: normaliseVerdictStatus(po.recommendation_status),
        recommendation_reasoning:
          typeof po.recommendation_reasoning === 'string'
            ? po.recommendation_reasoning.trim()
            : '',
      } as PropertyInput;
    });
  } else {
    // READ-COMPAT shim — a legacy / empty intake → one synthesised property.
    // Bug 2: the synthesised candidate gets a STABLE id deterministic from the
    // engagement scope (clientId+projectId) — NOT a fresh random newId('prop') on
    // every load — so a transient empty `answers.properties` (e.g. a thin
    // candidate dropped + saved as []) cannot churn the candidate identity and
    // silently invalidate `recommended_property_id`. Falls back to a random id
    // only when no scope is available (no stable seed to derive from).
    const legacy = staffFormFrom(a, north);
    const seed = `${scope?.clientId || ''}:${scope?.projectId || ''}`;
    const synthPropertyId =
      typeof a.propertyId === 'string' && a.propertyId ? a.propertyId : '';
    properties = [
      {
        id:
          scope && (scope.clientId || scope.projectId)
            ? stableId('prop', seed)
            : newId('prop'),
        // carry the FK when the legacy doc resolves one, so the synthesised row is
        // a real linked candidate (kept by buildPropertiesAnswer's hasLink filter).
        ...(synthPropertyId ? { propertyId: synthPropertyId } : {}),
        label: '',
        property_address:
          typeof a.property_address === 'string' ? a.property_address.trim() : '',
        north_rotation: engagementNorthStr,
        floors: legacy.floors,
        ...(Array.isArray(a.media) ? { media: a.media as MediaInput[] } : {}),
        site_location: siteLocationFromAnswer(a.site_location),
        // legacy single-property docs carry no per-property recommendation.
        recommendation_status: normaliseVerdictStatus(a.recommendation_status),
        recommendation_reasoning:
          typeof a.recommendation_reasoning === 'string'
            ? a.recommendation_reasoning.trim()
            : '',
      },
    ];
  }

  return { north_rotation: engagementNorthStr, properties };
}

/** Local helper — hydrate a SiteLocation from a stored answers value (or null).
 *  Defined here (not imported) to keep the pure block free of the impure
 *  `siteLocationFromDoc` import; uses the same parser at the impure boundary. */
function siteLocationFromAnswer(v: unknown): SiteLocation | null {
  if (!v || typeof v !== 'object') return null;
  return v as SiteLocation;
}

/**
 * PURE — hydrate the staff form from an existing intake doc's `answers.floors`
 * + a north value (from the client root profile). Defensive across a partial
 * doc. Reads ONLY the staff-owned fields; ignores client keys + boundary/
 * fixtures (Slices 1/3/4) which are not part of this form.
 */
export function staffFormFrom(
  answers: Record<string, unknown> | null | undefined,
  north: unknown,
): StaffIntakeForm {
  const a = answers || {};
  const floorsRaw = Array.isArray(a.floors) ? a.floors : [];
  const floors: FloorInput[] = floorsRaw.map((f) => {
    const o = (f || {}) as Record<string, unknown>;
    const entRaw = Array.isArray(o.entrances) ? o.entrances : [];
    const entrances: EntranceInput[] = entRaw.map((e) => {
      const eo = (e || {}) as Record<string, unknown>;
      const ptRaw = (eo.point || null) as Record<string, unknown> | null;
      const point =
        ptRaw &&
        typeof ptRaw.x === 'number' &&
        typeof ptRaw.y === 'number' &&
        Number.isFinite(ptRaw.x) &&
        Number.isFinite(ptRaw.y)
          ? { x: ptRaw.x as number, y: ptRaw.y as number }
          : undefined;
      return {
        label: typeof eo.label === 'string' ? eo.label : '',
        name: typeof eo.name === 'string' ? eo.name : '',
        type: isEntranceType(eo.type) ? eo.type : '',
        ...(point ? { point } : {}),
      };
    });
    const boundaryRaw = Array.isArray(o.boundary) ? o.boundary : null;
    const boundary = isUsableBoundary(boundaryRaw)
      ? boundaryRaw.map((p) => ({ x: p.x, y: p.y }))
      : null;
    const doorsRaw = Array.isArray(o.doors) ? o.doors : [];
    const doors: DoorInput[] = doorsRaw
      .map((d) => {
        const dd = (d || {}) as Record<string, unknown>;
        // placement defaults to 'perimeter' so legacy doors (saved before the
        // internal-door feature) hydrate exactly as before.
        const placement: DoorPlacement =
          dd.placement === 'internal' ? 'internal' : 'perimeter';
        const type = isEntranceType(dd.type) ? dd.type : '';
        const name = typeof dd.name === 'string' ? dd.name : '';
        const principal = dd.principal === true;
        if (placement === 'internal') {
          const width =
            typeof dd.width_image_px === 'number' &&
            Number.isFinite(dd.width_image_px)
              ? (dd.width_image_px as number)
              : 0;
          const aRaw = (dd.a || null) as Record<string, unknown> | null;
          const bRaw = (dd.b || null) as Record<string, unknown> | null;
          if (isFinitePt(aRaw) && isFinitePt(bRaw)) {
            // segment model (rectangular, rotatable internal door).
            return {
              placement: 'internal',
              a: { x: aRaw.x, y: aRaw.y },
              b: { x: bRaw.x, y: bRaw.y },
              width_image_px: width,
              type,
              name,
              principal,
            } as DoorInput;
          }
          // LEGACY point internal door → MIGRATE to a tiny segment centred on the
          // old point (so it renders as a rectangle and keeps its direction-pada).
          const atRaw = (dd.at || null) as Record<string, unknown> | null;
          if (isFinitePt(atRaw)) {
            const half = INTERNAL_DOOR_WIDTH_PX / 2;
            return {
              placement: 'internal',
              a: { x: atRaw.x - half, y: atRaw.y },
              b: { x: atRaw.x + half, y: atRaw.y },
              width_image_px: INTERNAL_DOOR_WIDTH_PX,
              type,
              name,
              principal,
            } as DoorInput;
          }
          return null;
        }
        if (
          typeof dd.edge_index !== 'number' ||
          !Number.isFinite(dd.edge_index) ||
          typeof dd.parametric_t !== 'number' ||
          !Number.isFinite(dd.parametric_t)
        ) {
          return null;
        }
        return {
          placement: 'perimeter',
          edge_index: dd.edge_index as number,
          parametric_t: dd.parametric_t as number,
          width_image_px:
            typeof dd.width_image_px === 'number' &&
            Number.isFinite(dd.width_image_px)
              ? (dd.width_image_px as number)
              : 0,
          type,
          name,
          principal,
        } as DoorInput;
      })
      .filter((x): x is DoorInput => x != null);
    // Slice-5 AUO fixtures hydrate (the confirm-loop). Defensive across a
    // partial doc; keeps all statuses so the review list round-trips. ⭐ BUG FIX
    // (2026-06-08): keep a placeable marker (valid point) even when it is still
    // NAMELESS, so a freshly-placed-then-saved AUO comes back as a to-be-named
    // card (mirrors the writer fix). Drop only a degenerate fixture with NEITHER
    // a renderable point NOR an analysable name+zone.
    const fxRaw = Array.isArray(o.fixtures) ? o.fixtures : [];
    const fixtures: FixtureInput[] = fxRaw
      .map((fx) => {
        const fo = (fx || {}) as Record<string, unknown>;
        const name = typeof fo.name === 'string' ? fo.name : '';
        const zoneRaw = typeof fo.zone === 'string' ? fo.zone.toUpperCase() : '';
        const zone = isZoneCode(zoneRaw) ? zoneRaw : '';
        const zones = Array.isArray(fo.zones)
          ? Array.from(
              new Set(
                (fo.zones as unknown[])
                  .map((z) => (typeof z === 'string' ? z.toUpperCase() : ''))
                  .filter((z) => isZoneCode(z)),
              ),
            )
          : [];
        const ptRaw = (fo.point || null) as Record<string, unknown> | null;
        const point =
          ptRaw &&
          typeof ptRaw.x === 'number' &&
          typeof ptRaw.y === 'number' &&
          Number.isFinite(ptRaw.x) &&
          Number.isFinite(ptRaw.y)
            ? { x: ptRaw.x as number, y: ptRaw.y as number }
            : undefined;
        // keep a renderable marker (point) OR an analysable fixture (name + ≥1 zone).
        if (!point && (!name.trim() || (!zone && zones.length === 0))) return null;
        const out: FixtureInput = { name, zone: zone || zones[0] || '' };
        if (zones.length) out.zones = zones;
        if (typeof fo.type === 'string') out.type = fo.type;
        if (typeof fo.status === 'string') out.status = fo.status;
        if (typeof fo.source === 'string') out.source = fo.source;
        if (point) out.point = point;
        if (fo.region && typeof fo.region === 'object') out.region = fo.region;
        if (fo.suggestionsByZone && typeof fo.suggestionsByZone === 'object')
          out.suggestionsByZone = fo.suggestionsByZone;
        if (typeof fo.rejected_by === 'string') out.rejected_by = fo.rejected_by;
        if (typeof fo.rejected_at === 'string') out.rejected_at = fo.rejected_at;
        return out;
      })
      .filter((x): x is FixtureInput => x != null);
    // Eval-canvas MARKUPS hydrate — verbatim, defensive across a partial doc.
    const mkRaw = Array.isArray(o.markups) ? o.markups : [];
    const markups = mkRaw
      .map((mk) => {
        const mo = (mk || {}) as Record<string, unknown>;
        const id = typeof mo.id === 'string' ? mo.id : '';
        const type = typeof mo.type === 'string' ? mo.type : '';
        if (!id || !type || typeof mo.x !== 'number' || typeof mo.y !== 'number') return null;
        const out: {
          id: string; type: string; x: number; y: number;
          w?: number; h?: number; x2?: number; y2?: number; note?: string;
        } = { id, type, x: mo.x as number, y: mo.y as number };
        if (typeof mo.w === 'number') out.w = mo.w as number;
        if (typeof mo.h === 'number') out.h = mo.h as number;
        if (typeof mo.x2 === 'number') out.x2 = mo.x2 as number;
        if (typeof mo.y2 === 'number') out.y2 = mo.y2 as number;
        if (typeof mo.note === 'string') out.note = mo.note as string;
        return out;
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
    return {
      id: typeof o.id === 'string' && o.id ? o.id : newId('f'),
      name: typeof o.name === 'string' ? o.name : '',
      main_entry_label:
        typeof o.main_entry_label === 'string' ? o.main_entry_label : '',
      entrances,
      doors,
      floorplan: floorPlanFromDoc(
        (o.floorplan as Record<string, unknown>) || null,
      ),
      boundary,
      fixtures,
      markups,
    };
  });
  // `north` is the STORED engine-rotation (clients/{id}.profile.north_rotation).
  // The form field is OWNER-FACING (the true bearing), so convert rotation →
  // bearing for display. The single UI↔storage boundary (item 1 fix).
  const b = rotationToBearing(north);
  return {
    north_rotation: b == null ? '' : String(b),
    floors,
  };
}

/* ─────────────────────────── impure (Firestore) ───────────────────────── */
/* Everything below imports `db`; the Node test runner never loads it (the
 * tests import only the pure exports above via _tsload). Keep all Firebase
 * access at the bottom. */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { siteLocationFromDoc } from './siteLocation';
import { asScope } from './intake';
import type { IntakeScope } from './intakeScope';
import { loadIntakeDoc, writeIntakeDoc } from './intakeScopeDb';
// PURE planner for the Lane-A stamp clear (candidatePropertyJoin imports only a
// TYPE from this file, so the runtime dependency is one-way — no cycle).
import { clearCandidateStamps } from './candidatePropertyJoin';

/**
 * Load the staff-technical view: the intake doc's `answers.floors` +
 * `answers.site_location` and the north bearing from the client ROOT profile.
 * Returns blanks when nothing exists yet.
 */
export async function loadStaffIntake(
  scopeOrClientId: IntakeScope | string,
  tier: string = L2B_R1_TIER,
): Promise<{
  form: StaffIntakeForm;
  site: SiteLocation;
  northShot: NorthScreenshot | null;
  /** the client-filled project_type (drives the AUO palette). '' when unset. */
  projectType: string;
  /** Slice-7b report-context fields (presenting concerns + property address). */
  reportContext: ReportContextForm;
}> {
  const scope = asScope(scopeOrClientId);
  const clientId = scope.clientId;
  const [intakeHit, rootSnap] = await Promise.all([
    loadIntakeDoc(scope, tier),
    getDoc(doc(db, 'clients', clientId)),
  ]);
  const answers = intakeHit
    ? (intakeHit.data.answers as Record<string, unknown>)
    : null;
  const root = rootSnap.exists()
    ? (rootSnap.data() as Record<string, unknown>)
    : {};
  const profile = (root.profile as Record<string, unknown>) || {};
  const north = profile.north_rotation;
  const site = siteLocationFromDoc(
    (answers?.site_location as Record<string, unknown>) || null,
  );
  // The true-north Google-Earth screenshot ref (durable; survives reload). Lives
  // on the client root profile beside north_rotation (both are "orientation").
  const northShot = northScreenshotFromDoc(
    (profile.north_screenshot as Record<string, unknown>) || null,
  );
  // project_type lives in the client-filled answers (Slice 1) — drives the AUO
  // palette. Tolerate a missing key (returns '' → palette falls back to residence).
  const projectType =
    typeof answers?.project_type === 'string' ? (answers.project_type as string) : '';
  // Slice-7b report context (presenting concerns + property address) — same
  // `answers` map, hydrated from the intake doc so it round-trips on reload.
  const reportContext = reportContextFrom(
    (answers as Record<string, unknown>) || null,
  );
  return { form: staffFormFrom(answers, north), site, northShot, projectType, reportContext };
}

/**
 * Write the north bearing to the client ROOT doc at `profile.north_rotation`
 * (Break 1 fix). The caller passes the owner's TRUE-NORTH BEARING; we convert
 * it to the engine-rotation `(360 − bearing)` and store THAT (item 1 fix —
 * single UI↔storage boundary, so the engine + the on-screen grid both orient
 * correctly to the owner's bearing with no engine change). MERGE so the rest of
 * the client master (name/phone/coBrand…) is untouched. A null/blank bearing
 * writes nothing (no-op) — we never persist a junk number.
 */
export async function saveNorthRotation(
  clientId: string,
  bearingDegrees: number,
  actor: { uid?: string | null; name?: string | null },
): Promise<void> {
  const rotation = bearingToRotation(bearingDegrees);
  if (rotation == null) throw new Error('North bearing must be a number 0–360.');
  await setDoc(
    doc(db, 'clients', clientId),
    {
      profile: { north_rotation: rotation },
      northUpdatedBy: actor.name || actor.uid || null,
      northUpdatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * Merge-write the true-north Google-Earth screenshot ref to the client ROOT doc
 * at `profile.north_screenshot` (16th dispatch). MERGE so north_rotation + the
 * rest of the profile are untouched. The image bytes are already in Storage
 * (uploadNorthScreenshot); this persists only the durable {storage_path,
 * download_url, uploaded_at} reference so it survives reload + is report-
 * accessible. Passing `null` CLEARS the ref (remove screenshot).
 */
export async function saveNorthScreenshot(
  clientId: string,
  shot: NorthScreenshot | null,
  actor: { uid?: string | null; name?: string | null },
): Promise<void> {
  await setDoc(
    doc(db, 'clients', clientId),
    {
      profile: { north_screenshot: shot ? northScreenshotDocBody(shot) : null },
      northUpdatedBy: actor.name || actor.uid || null,
      northUpdatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * Merge-write the staff floor scaffold to `answers.floors` on the intake doc.
 * MERGE at the `answers` map level keeps the client-owned `answers.*` keys
 * (client_name, presenting_concerns …) AND `answers.site_location`; only
 * `answers.floors` is replaced (a Firestore array merge is wholesale, which is
 * correct — staff own the whole floor list each save). Stage-1 (Slice 3)
 * FULFILS the one-owner contract: each floor object now carries its own
 * `boundary` (≥3 {x,y} pts the engine grids) + `floorplan` (image metadata),
 * emitted by `floorToAnswer`. Because they ride INSIDE the floor objects, the
 * wholesale array replace preserves them — there is no separate per-floor
 * boundary merge that could be clobbered.
 */
export async function saveFloors(
  scopeOrClientId: IntakeScope | string,
  form: StaffIntakeForm,
  actor: { uid?: string | null; via: 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  const floors = buildFloorsAnswer(form);
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: 'L2B/v2',
    tier: L2B_R1_TIER,
    answers: { floors },
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/**
 * MULTI-PROPERTY save (L1A — owner ruling D1). Merge-write `answers.properties`
 * (the full candidate-property array) to the intake doc, AND keep
 * `answers.floors` = `properties[0].floors` so the single-property/L2B read
 * path + the engine's own read-compat shim still see a `floors[]`. Both ride on
 * the SAME `answers` map, merge-written so they never clobber the client keys /
 * site_location / report-context.
 *
 * Uses nested-object notation (same as saveFloors) so setDoc deep-merges
 * `answers.properties`, `answers.floors`, and `answers.recommended_property_id`
 * into the existing `answers` map without overwriting sibling keys.
 *
 * `recommended_property_id` (the §D cross-property recommendation pick) rides
 * here too when supplied — a sibling `answers` string the L1A template reads.
 */
export async function saveProperties(
  scopeOrClientId: IntakeScope | string,
  form: PropertiesForm,
  actor: { uid?: string | null; via: 'staff' },
  opts: { recommendedPropertyId?: string | null } = {},
  tier: string = L2B_R1_TIER,
): Promise<void> {
  const properties = buildPropertiesAnswer(form);
  // back-compat: the first property's floors mirror to answers.floors so the
  // L2B template + the engine shim see a floors[] for a single-property read.
  const firstFloors =
    (properties[0] && (properties[0] as { floors?: unknown }).floors) || [];
  const recommended =
    typeof opts.recommendedPropertyId === 'string'
      ? opts.recommendedPropertyId.trim()
      : '';
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: 'L2B/v2',
    tier: L2B_R1_TIER,
    // Nested-object notation: setDoc deep-merges into the answers map without
    // overwriting sibling keys (site_location, project_type, etc.).
    answers: {
      properties,
      floors: firstFloors,
      recommended_property_id: recommended,
    },
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/**
 * DISJOINT write of ONLY the §D cross-property winner (`answers.recommended_property_id`).
 * Used by the Verdict stage screen (VerdictField), where the §D pick was relocated
 * from the Evaluate screen (2026-07-02). It writes the SAME field `saveProperties`
 * does — but as a single nested leaf, so `writeIntakeDoc`'s setDoc(merge:true) deep-
 * merges it WITHOUT touching `answers.properties` / `answers.floors` (the Verdict
 * screen holds no live floor buffer, so it must not re-serialise the properties array).
 * Value stored identically: a trimmed string (empty = "set at curation").
 */
export async function saveRecommendedProperty(
  scopeOrClientId: IntakeScope | string,
  recommendedPropertyId: string | null,
  actor: { uid?: string | null; via: 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  const recommended =
    typeof recommendedPropertyId === 'string' ? recommendedPropertyId.trim() : '';
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: 'L2B/v2',
    tier: L2B_R1_TIER,
    answers: {
      recommended_property_id: recommended,
    },
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/**
 * Load the multi-property staff view: hydrate `answers.properties[]` (read-compat
 * one-element for a legacy single-property doc) + the engagement north +
 * `recommended_property_id`. Companion to `loadStaffIntake` (which still returns
 * the legacy single-property `form` for callers that have not migrated).
 */
export async function loadPropertiesIntake(
  scopeOrClientId: IntakeScope | string,
  tier: string = L2B_R1_TIER,
): Promise<{
  form: PropertiesForm;
  recommendedPropertyId: string;
  site: SiteLocation;
  northShot: NorthScreenshot | null;
  projectType: string;
  reportContext: ReportContextForm;
}> {
  const scope = asScope(scopeOrClientId);
  const clientId = scope.clientId;
  const [intakeHit, rootSnap] = await Promise.all([
    loadIntakeDoc(scope, tier),
    getDoc(doc(db, 'clients', clientId)),
  ]);
  let answers: Record<string, unknown> | null = null;
  if (intakeHit) {
    const rawDoc = intakeHit.data;
    const nestedAnswers = (rawDoc.answers as Record<string, unknown>) || {};
    // Back-compat: legacy docs have dotted-key TOP-LEVEL fields ("answers.properties"
    // etc.) written before the setDoc→nested fix. Spread them FIRST as a fallback,
    // then let nestedAnswers override — so a new save (which writes to the nested
    // answers map) always wins over the old dotted-key remnant.
    answers = {
      ...(rawDoc['answers.properties'] !== undefined
        ? { properties: rawDoc['answers.properties'] }
        : {}),
      ...(rawDoc['answers.floors'] !== undefined
        ? { floors: rawDoc['answers.floors'] }
        : {}),
      ...(rawDoc['answers.recommended_property_id'] !== undefined
        ? { recommended_property_id: rawDoc['answers.recommended_property_id'] }
        : {}),
      // nestedAnswers last — newest save always wins.
      ...nestedAnswers,
    };
  }
  const root = rootSnap.exists() ? (rootSnap.data() as Record<string, unknown>) : {};
  const profile = (root.profile as Record<string, unknown>) || {};
  const north = profile.north_rotation;
  const site = siteLocationFromDoc(
    (answers?.site_location as Record<string, unknown>) || null,
  );
  const northShot = northScreenshotFromDoc(
    (profile.north_screenshot as Record<string, unknown>) || null,
  );
  const projectType =
    typeof answers?.project_type === 'string' ? (answers.project_type as string) : '';
  const reportContext = reportContextFrom((answers as Record<string, unknown>) || null);
  const recommendedPropertyId =
    typeof answers?.recommended_property_id === 'string'
      ? (answers.recommended_property_id as string)
      : '';
  return {
    form: propertiesFormFrom(answers, north, {
      clientId: scope.clientId,
      projectId: scope.projectId,
    }),
    recommendedPropertyId,
    site,
    northShot,
    projectType,
    reportContext,
  };
}

/**
 * TWO-LANES CONSISTENCY (owner bug 2026-07-03) — clear the Lane-A candidate
 * STAMP (`answers.properties[i].propertyId`) for a property that was unlinked /
 * removed from the registry. `unlinkPropertyFromProject` only cleans Lane B
 * (`projects.propertyRefs[]` + `properties.engagementRefs[]`); without this the
 * intake keeps showing "✓ auto-linked P#####" for a link that no longer exists.
 *
 * Load → pure `clearCandidateStamps` → persist via the SAME `saveProperties`
 * array-replace every candidate write uses (preserving the §D recommended pick
 * + the engagement north). Returns true when a stamp was actually cleared.
 * Best-effort by contract: callers treat a failure as non-fatal (the unlink /
 * trash itself must still succeed) — they catch + console.warn.
 */
export async function clearCandidatePropertyStamp(
  scopeOrClientId: IntakeScope | string,
  tier: string,
  propertyId: string,
): Promise<boolean> {
  const scope = asScope(scopeOrClientId);
  const { form, recommendedPropertyId } = await loadPropertiesIntake(scope, tier);
  const { next, changed } = clearCandidateStamps(form.properties, propertyId);
  if (!changed) return false;
  await saveProperties(
    scope,
    { north_rotation: form.north_rotation, properties: next },
    { uid: null, via: 'staff' },
    { recommendedPropertyId: recommendedPropertyId || '' },
    tier,
  );
  return true;
}

/**
 * Merge-write `answers.site_location` to the intake doc (owner-locked
 * convenience; the engine ignores this key). Merge keeps every other
 * `answers.*` key intact.
 */
export async function saveSiteLocation(
  scopeOrClientId: IntakeScope | string,
  site: SiteLocation,
  actor: { uid?: string | null; via: 'staff' | 'client' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: 'L2B/v2',
    tier: L2B_R1_TIER,
    answers: { site_location: site },
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/**
 * Merge-write the report context to the intake doc: the Slice-7b fields
 * (`answers.presenting_concerns` + `answers.property_address`) PLUS the curation
 * verdict (`answers.verdict` + `answers.verdict_reasoning` +
 * `answers.verdict_status`). Merge at the `answers` map level keeps `floors` /
 * `site_location` / the client-owned `answers.*` keys intact — only these keys
 * are replaced. `presenting_concerns` is written as an ARRAY (the shape report
 * §A reads, one bullet per element); `property_address` / `verdict` /
 * `verdict_reasoning` as trimmed strings (blank → the report degrades
 * gracefully: a blank address derives a `site_location` fallback; a blank
 * verdict keeps the L1A §D curation frame); `verdict_status` as a validated
 * enum string ('' = no chip). Also `answers.room_overrides` (the curation
 * room-allocation override map, written via a dotted path so it replaces
 * wholesale). The engine ignores the report-context keys; `room_overrides` is
 * read by the §E/§F family reconciler (an override wins over the engine draft).
 */
export async function saveReportContext(
  scopeOrClientId: IntakeScope | string,
  form: ReportContextForm,
  actor: { uid?: string | null; via: 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  const {
    presenting_concerns,
    property_address,
    verdict,
    verdict_reasoning,
    verdict_status,
    room_overrides,
  } = buildReportContextAnswer(form);
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: 'L2B/v2',
    tier: L2B_R1_TIER,
    answers: {
      presenting_concerns,
      property_address,
      verdict,
      verdict_reasoning,
      verdict_status,
    },
    // `room_overrides` is written via a DOTTED field path so the map is
    // REPLACED wholesale (a nested-map merge would never drop a removed
    // member's stale override). Lives under the same `answers` map — still
    // never touches floors / site_location / verdict / the client keys.
    'answers.room_overrides': room_overrides,
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/**
 * DISJOINT report-context saves (clean-flow §6 split). The L1A flow surfaces the
 * VERDICT in Step 5 and the REST of report-context (concerns · address · room
 * overrides) in the "more" drawer — two SEPARATE surfaces over the ONE intake
 * doc. To avoid a clobber, each writes ONLY its owned leaves: `writeIntakeDoc`
 * uses `setDoc(merge:true)`, so a nested `answers.{verdict,…}` write deep-merges
 * those leaves and LEAVES `answers.{presenting_concerns,property_address,
 * room_overrides}` untouched (and vice-versa). Verified: writeIntakeDoc merge +
 * the existing `answers.room_overrides` dotted-path replace. Serialisation reuses
 * `buildReportContextAnswer` so the composer reads identical shapes.
 */
export async function saveVerdict(
  scopeOrClientId: IntakeScope | string,
  v: { verdict_text: string; verdict_reasoning_text: string; verdict_status: VerdictStatus },
  actor: { uid?: string | null; via: 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  const built = buildReportContextAnswer({
    ...blankReportContext(),
    verdict_text: v.verdict_text,
    verdict_reasoning_text: v.verdict_reasoning_text,
    verdict_status: v.verdict_status,
  });
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: 'L2B/v2',
    tier: L2B_R1_TIER,
    answers: {
      verdict: built.verdict,
      verdict_reasoning: built.verdict_reasoning,
      verdict_status: built.verdict_status,
    },
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

export async function saveReportContextRest(
  scopeOrClientId: IntakeScope | string,
  form: {
    presenting_concerns_text: string;
    property_address: string;
    room_overrides: RoomOverrideRow[];
  },
  actor: { uid?: string | null; via: 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  const built = buildReportContextAnswer({
    ...blankReportContext(),
    presenting_concerns_text: form.presenting_concerns_text,
    property_address: form.property_address,
    room_overrides: form.room_overrides,
  });
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: 'L2B/v2',
    tier: L2B_R1_TIER,
    answers: {
      presenting_concerns: built.presenting_concerns,
      property_address: built.property_address,
    },
    // dotted path → the room_overrides map is REPLACED wholesale (matches
    // saveReportContext; a nested-map merge would keep stale removed members).
    'answers.room_overrides': built.room_overrides,
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

// ─── Module 4 backward-compat shim (AD-013) ───────────────────────────────

/**
 * Resolve the floor data to pass to the report engine.
 *
 * **Source of truth depends on whether the project has linked Property
 * entities (Module 4, AD-013):**
 *
 * - `project.propertyRefs` present + non-empty → read `PropertyFloor[]` from
 *   each `properties/{pid}` doc in Firestore and map to `FloorInput[]`.
 *   The active plan version on each floor becomes the `floorplan`. The
 *   boundary is mapped from `PropertyFloor.boundary` (Pt2D[] → Pt[]).
 *
 * - `project.propertyRefs` absent or empty → fall back to reading
 *   `answers.floors` from the intake doc exactly as before. All existing
 *   projects (pre-Module 4) continue to work without any migration.
 *
 * Callers in the report assembly chain should call this once and use the
 * returned `FloorInput[]` — they should never branch on `propertyRefs`
 * themselves.
 */
export async function resolveFloorData(
  project: { projectId?: string; clientId?: string | null; propertyRefs?: Array<{ propertyId: string; role: string }> },
  intakeTier = 'L2B_R1',
): Promise<FloorInput[]> {
  const refs = project.propertyRefs;

  // ── PATH A: Property entity path (Module 4) ──────────────────────────────
  if (refs && refs.length > 0) {
    const floors: FloorInput[] = [];
    for (const ref of refs) {
      const propDoc = await getDoc(doc(db, 'properties', ref.propertyId));
      if (!propDoc.exists()) continue;
      const d = propDoc.data();
      const propFloors: Array<Record<string, unknown>> = Array.isArray(d['floors'])
        ? (d['floors'] as Array<Record<string, unknown>>)
        : [];

      for (const pf of propFloors) {
        const floorId = String(pf['floorId'] ?? '');
        const label = String(pf['label'] ?? floorId);

        // Active plan version → FloorPlan shape
        const plans = Array.isArray(pf['plans'])
          ? (pf['plans'] as Array<Record<string, unknown>>)
          : [];
        const activePlan = plans.find((v) => v['isActive']);
        const floorplan: FloorPlan | null = activePlan
          ? {
              downloadURL: String(activePlan['imageUrl'] ?? ''),
              storagePath: String(activePlan['storagePath'] ?? ''),
              contentType: String(activePlan['contentType'] ?? 'image/jpeg'),
              width: Number(activePlan['width'] ?? 0),
              height: Number(activePlan['height'] ?? 0),
            }
          : null;

        // Boundary: PropertyFloor uses Pt2D {x,y}; FloorInput uses Pt (same shape)
        const rawBoundary = Array.isArray(pf['boundary'])
          ? (pf['boundary'] as Array<{ x: number; y: number }>)
          : null;
        const boundary: Pt[] | null = rawBoundary
          ? rawBoundary.map((pt) => ({ x: pt.x, y: pt.y }))
          : null;

        floors.push({
          id: floorId,
          name: label,
          entrances: [],          // entrance marks are still on the intake doc
          floorplan,
          boundary: boundary && boundary.length >= 3 ? boundary : null,
        });
      }
    }
    return floors;
  }

  // ── PATH B: intake doc path (all pre-Module 4 projects) ──────────────────
  // AD-030: read the project-scoped intake doc first, falling back to the legacy
  // client-scoped doc. `loadIntakeDoc` handles both; a project without a clientId
  // and without a projectId has nowhere to read from.
  if (!project.clientId) return [];
  const intakeHit = await loadIntakeDoc(
    { clientId: project.clientId, projectId: project.projectId },
    intakeTier,
  );
  if (!intakeHit) return [];
  const answers = intakeHit.data['answers'] as Record<string, unknown> | undefined;
  if (!answers) return [];
  const floorsRaw = Array.isArray(answers['floors'])
    ? (answers['floors'] as Array<Record<string, unknown>>)
    : [];
  return floorsRaw.map((f) => {
    const fp = f['floorplan'] as Record<string, unknown> | null | undefined;
    return {
      id: String(f['id'] ?? ''),
      name: String(f['name'] ?? ''),
      main_entry_label: f['main_entry_label'] ? String(f['main_entry_label']) : undefined,
      entrances: Array.isArray(f['entrances'])
        ? (f['entrances'] as EntranceInput[])
        : [],
      floorplan: fp
        ? {
            downloadURL: String(fp['downloadURL'] ?? fp['url'] ?? ''),
            storagePath: String(fp['storagePath'] ?? fp['storage_path'] ?? ''),
            contentType: String(fp['contentType'] ?? fp['content_type'] ?? 'image/jpeg'),
            width: Number(fp['width'] ?? 0),
            height: Number(fp['height'] ?? 0),
          }
        : null,
      boundary: Array.isArray(f['boundary'])
        ? (f['boundary'] as Pt[])
        : null,
    };
  });
}
